import type { ReactNode } from "react";
import { useRef } from "react";
import { AutoSizer, List } from "react-virtualized";

type VirtualizedListProps<T> = {
  readonly items: readonly T[];
  /** Fixed px height, or a per-row height for mixed header/row lists. */
  readonly rowHeight: number | ((item: T, index: number) => number);
  readonly renderRow: (item: T, index: number) => ReactNode;
  /** Lazy loading: called once whenever the viewport nears the end of `items`. */
  readonly onEndReached?: () => void;
  /** How many rows from the end trigger `onEndReached` (default 10). */
  readonly endThreshold?: number;
  /** Accessible name for the list. */
  readonly ariaLabel?: string;
};

/**
 * The feature's one list primitive — react-virtualized `AutoSizer` + `List`, the same library the
 * app's SelectDropDown/DropdownListComponent wrap. Every Portal Chat list (conversations,
 * people pickers, members, attachments) renders through this so a portal with thousands of rows
 * mounts only the visible window into the DOM.
 */
export function VirtualizedList<T>({
  items,
  rowHeight,
  renderRow,
  onEndReached,
  endThreshold = 10,
  ariaLabel,
}: VirtualizedListProps<T>) {
  // onRowsRendered fires per scroll frame — request the next page once per end-approach.
  const lastRequestedAtCountRef = useRef(-1);

  return (
    <AutoSizer>
      {({ width, height }) => (
        <List
          aria-label={ariaLabel}
          width={width}
          height={height}
          rowCount={items.length}
          rowHeight={
            typeof rowHeight === "number"
              ? rowHeight
              : ({ index }) => {
                  const item = items[index];
                  return item === undefined ? 0 : rowHeight(item, index);
                }
          }
          onRowsRendered={
            onEndReached
              ? ({ stopIndex }) => {
                  if (stopIndex >= items.length - endThreshold && lastRequestedAtCountRef.current !== items.length) {
                    lastRequestedAtCountRef.current = items.length;
                    onEndReached();
                  }
                }
              : undefined
          }
          rowRenderer={({ index, style, key }) => {
            const item = items[index];
            if (item === undefined) return null;
            // react-virtualized positions rows absolutely via `style` — it must land on the
            // outermost element of every row.
            return (
              <div key={key} style={style}>
                {renderRow(item, index)}
              </div>
            );
          }}
        />
      )}
    </AutoSizer>
  );
}
